import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TYPE } from 'theme';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import user from 'stores/user';
import { LuckyDrawRewardStats, LuckyDrawReward } from 'gql-ops/graph/__generated__/request';
import luckyDrawStore from 'stores/luckyDrawStore';
import { ReactComponent as ShareIcon } from 'assets/images/lucky-draw/share.svg';
import MaterialTablePagination from 'components/MaterialTablePagination';
import { NETWORK_CONFIG } from 'constant';
import { CircularProgress } from '@mui/material';
import BigNumber from 'bignumber.js';

const HistoryContainer = styled.div`
    background-color: #000000cc;
    padding: 40px;
    border-radius: 24px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;

    @media (max-width: 768px) {
        padding: 20px;
        border-radius: 16px;
    }

    @media (max-width: 480px) {
        padding: 16px;
        border-radius: 12px;
    }
`;

const TotalPrizeGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 40px;
    background-color: rgba(16, 16, 16, 1);
    border-radius: 16px;
    padding: 20px;
`;

const PrizeCard = styled.div`
    border-right: 1px dashed rgba(50, 50, 50, 1);
    &:last-child {
        border-right: none;
    }
`;

const TableContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    overflow-y: visible;
    border-radius: 12px;
    background: #000;
    position: relative;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(106, 13, 173, 0.5);
        border-radius: 4px;

        &:hover {
            background: rgba(106, 13, 173, 0.7);
        }
    }

    @media (max-width: 768px) {
        overflow-x: scroll;
        -webkit-overflow-scrolling: touch;

        &::-webkit-scrollbar {
            height: 8px;
        }
    }
`;

const TableContent = styled.div`
    min-width: 900px;
    padding: 24px;
    border-radius: 12px;
    background: #000;
    display: table;
    table-layout: auto;
    border-collapse: separate;
    border-spacing: 0 8px;
    width: fit-content;

    @media (max-width: 768px) {
        min-width: 900px;
        width: 900px;
        padding: 16px;
    }
`;

const HistoryRow = styled.div`
    display: table-row;
    background: transparent;
    border-radius: 8px;
    transition: background 0.2s ease;

    &:hover {
        background: rgba(40, 40, 40, 0.9);
    }

    & > * {
        background: transparent !important;
    }

    /* First cell rounded left corners */
    & > *:first-child {
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
    }

    /* Last cell rounded right corners */
    & > *:last-child {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
    }
`;

const DrawInfoWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const DrawInfo = styled.div`
    display: table-cell;
    padding: 16px 20px;
    vertical-align: middle;
    min-width: 120px;
    width: 120px;
`;

const DrawNumber = styled.div`
    color: #ffffff;
    font-weight: 700;
    font-size: 16px;
    line-height: 1.2;
`;

const DrawCountText = styled.div`
    color: #ffffff;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.2;
`;

const RewardColumnWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
`;

const RewardColumn = styled.div`
    display: table-cell;
    padding: 16px 20px;
    vertical-align: middle;
    min-width: 150px;
    white-space: nowrap;
`;

const RewardValue = styled.div<{ $color?: string }>`
    color: ${({ $color }) => $color || '#ffffff'};
    font-weight: 700;
    font-size: 16px;
    line-height: 1.2;
`;

const RewardLabel = styled.div`
    color: #999999;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.2;
`;

const ActionColumn = styled.div`
    display: table-cell;
    padding: 16px 20px;
    vertical-align: middle;
    width: 60px;
    text-align: center;
    cursor: pointer;
    transition: opacity 0.3s ease;

    &:hover {
        opacity: 0.7;
    }
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 240px;
`;

function History() {
    const { todayStats: totalPrizeStats, histories: historyRecords, historiesTotalCount, loading } = luckyDrawStore;
    const [currentPage, setCurrentPage] = useState(0);
    const pageSize = 10;

    const fetchHistoryData = useCallback(async () => {
        if (!user.address) return;
        try {
            await luckyDrawStore.fetchHistories(user.address, {
                limit: pageSize,
                page: currentPage + 1,
            });

            // Check if currentPage is beyond valid range based on API's totalCount
            const fetchedTotalCount = luckyDrawStore.historiesTotalCount;
            if (fetchedTotalCount > 0) {
                // Calculate the last valid page index (0-based)
                const maxValidPage = Math.max(0, Math.ceil(fetchedTotalCount / pageSize) - 1);

                // If currentPage exceeds the valid range, reset to last valid page
                if (currentPage > maxValidPage) {
                    console.warn(
                        '[History] Current page',
                        currentPage,
                        'exceeds valid range. Max valid page:',
                        maxValidPage,
                        'Total count:',
                        fetchedTotalCount,
                    );
                    setCurrentPage(maxValidPage);
                }
            } else if (fetchedTotalCount === 0 && currentPage > 0) {
                // No data at all, reset to first page
                setCurrentPage(0);
            }
        } catch (error) {
            console.error('Failed to fetch history data:', error);
        }
    }, [currentPage, pageSize]);

    // Reset pagination when address changes
    useEffect(() => {
        setCurrentPage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.address]);

    useEffect(() => {
        fetchHistoryData();
    }, [fetchHistoryData]);

    const handlePageChange = (_event: unknown, page: number) => {
        // Calculate max valid page based on current totalCount
        const maxValidPage = historiesTotalCount > 0 ? Math.max(0, Math.ceil(historiesTotalCount / pageSize) - 1) : 0;
        // Ensure page doesn't exceed valid range
        const validPage = Math.min(page, maxValidPage);
        setCurrentPage(validPage);
    };

    const handleShareClick = (txHash: string) => {
        if (!user.chainId || !txHash) return;
        const explorerUrl = `${NETWORK_CONFIG[user.chainId].explore}/tx/${txHash}`;
        window.open(explorerUrl, '_blank');
    };

    const PRIZE_TYPES = ['BTC', 'USDT', 'GOATED', 'Points'];

    const formatRewardValue = (
        prize: LuckyDrawRewardStats | null,
        prizes: LuckyDrawRewardStats[],
        type: string,
    ): { value: string; color: string } => {
        if (!prize) {
            return { value: '-', color: '#ffffff' };
        }

        // Ensure prizes is an array
        if (!Array.isArray(prizes) || prizes.length === 0) {
            return { value: '-', color: '#ffffff' };
        }

        // Find all matching prizes to calculate total amount correctly
        const matchingPrizes: Array<{ prize: LuckyDrawRewardStats; rewardDetail: LuckyDrawReward }> = [];
        prizes.forEach((p) => {
            const rewardDetail = luckyDrawStore.getRewardByIndex(p.index);
            if (!rewardDetail) return;

            const rewardKey = rewardDetail.key?.toLowerCase() || '';
            const typeLower = type.toLowerCase();

            // Match by key only (key is the standardized identifier)
            if (rewardKey === typeLower || rewardKey.includes(typeLower)) {
                matchingPrizes.push({ prize: p, rewardDetail });
            }
        });

        if (matchingPrizes.length === 0) {
            return { value: '-', color: '#ffffff' };
        }

        // Use the first matching reward detail for name and color
        const rewardDetail = matchingPrizes[0].rewardDetail;
        let color = '#ffffff';
        const rewardName = rewardDetail.name?.toLowerCase() || '';

        if (rewardName.includes('btc')) {
            color = '#F7931A';
        }

        // Calculate total amount by summing all matching prizes using BigNumber for precision
        // Important: Each prize entry should be calculated separately, even if they have the same index
        // Each reward may have different decimals, so convert each to actual amount first, then sum
        let totalAmountBN = new BigNumber(0);
        const decimals: number[] = [];

        matchingPrizes.forEach((item) => {
            if (!item.rewardDetail.amount) return;

            const decimal = item.rewardDetail.decimal || 18;
            decimals.push(decimal);

            // Convert raw amount to BigNumber
            const rawAmountBN = new BigNumber(item.rewardDetail.amount);
            const drawnCountBN = new BigNumber(item.prize.drawnCount);

            // First multiply by drawnCount (in raw format to preserve precision)
            const totalRawAmount = rawAmountBN.multipliedBy(drawnCountBN);

            // Then convert to actual amount by dividing by decimal
            const decimalDivisor = new BigNumber(10).pow(decimal);
            const actualAmount = totalRawAmount.dividedBy(decimalDivisor);

            // Add to total (in actual amount format)
            totalAmountBN = totalAmountBN.plus(actualAmount);
        });

        // Format the total amount with appropriate precision
        const maxDecimal = Math.max(...decimals, 18);
        const fixedDecimal = maxDecimal > 6 ? 6 : maxDecimal;
        const formattedTotal = totalAmountBN.toFixed(fixedDecimal);

        // Remove trailing zeros
        const cleanAmount = formattedTotal.replace(/\.?0+$/, '');

        return {
            value: `${cleanAmount} ${rewardDetail.name || ''}`,
            color,
        };
    };

    const getRewardLabel = useCallback((type: string): string => {
        const labels: Record<string, string> = {
            BTC: 'BTC Reward',
            USDT: 'USDT Reward',
            GOATED: 'GOATED Reward',
            Points: 'Points Reward',
        };
        return labels[type] || type;
    }, []);

    const getPrizeByType = useCallback((prizes: LuckyDrawRewardStats[], type: string): LuckyDrawRewardStats | null => {
        // Ensure prizes is an array
        if (!Array.isArray(prizes) || prizes.length === 0) return null;

        // Find all prizes that match the given type by checking their reward key
        const matchingPrizes: Array<{ prize: LuckyDrawRewardStats; rewardDetail: LuckyDrawReward }> = [];

        prizes.forEach((prize) => {
            const rewardDetail = luckyDrawStore.getRewardByIndex(prize.index);
            if (!rewardDetail) return;

            const rewardKey = rewardDetail.key?.toLowerCase() || '';
            const typeLower = type.toLowerCase();

            // Match by key only (key is the standardized identifier)
            // Use exact match or key contains type for flexibility (e.g., "BTC" matches "btc")
            if (rewardKey === typeLower || rewardKey.includes(typeLower)) {
                matchingPrizes.push({ prize, rewardDetail });
            }
        });

        if (matchingPrizes.length === 0) return null;

        // If only one prize, return it directly
        if (matchingPrizes.length === 1) return matchingPrizes[0].prize;

        // Sum up all matching prizes - calculate total amount across all matching prizes
        const totalDrawnCount = matchingPrizes.reduce((sum, item) => sum + item.prize.drawnCount, 0);

        // Use the first matching prize's structure, but with summed drawnCount
        return {
            ...matchingPrizes[0].prize,
            drawnCount: totalDrawnCount,
        };
    }, []);

    const totalStats = useMemo(() => {
        if (!totalPrizeStats) return null;

        const totals: Record<string, BigNumber> = {
            totalBTCPrize: new BigNumber(0),
            totalUSDTPrize: new BigNumber(0),
            totalPointsPrize: new BigNumber(0),
            totalNFTPrize: new BigNumber(0),
            totalGOATEDPrize: new BigNumber(0),
        };

        const typeMap: Record<string, string> = {
            btc: 'totalBTCPrize',
            usdt: 'totalUSDTPrize',
            point: 'totalPointsPrize',
            nft: 'totalNFTPrize',
            goated: 'totalGOATEDPrize',
        };

        totalPrizeStats.totalRewards.forEach((prize: LuckyDrawRewardStats) => {
            const rewardDetail = luckyDrawStore.getRewardByIndex(prize.index);
            if (!rewardDetail || !rewardDetail.amount) return;

            const type = rewardDetail.key?.toLowerCase() || '';
            const decimal = rewardDetail.decimal || 18;
            const totalAmount = new BigNumber(rewardDetail.amount)
                .dividedBy(new BigNumber(10).pow(decimal))
                .multipliedBy(new BigNumber(prize.drawnCount));

            // Find matching type and accumulate
            const matchedKey = Object.keys(typeMap).find((key) => type.includes(key));
            if (matchedKey) {
                totals[typeMap[matchedKey]] = totals[typeMap[matchedKey]].plus(totalAmount);
            }
        });

        // Format all totals
        return Object.keys(totals).reduce((acc, key) => {
            acc[key] = totals[key].toFixed(6).replace(/\.?0+$/, '') || '0';
            return acc;
        }, {} as Record<string, string>);
    }, [totalPrizeStats]);

    return (
        <HistoryContainer>
            <TYPE.title fontSize={24} fontWeight={700} width="fit-content" marginBottom="40px">
                My Records
            </TYPE.title>
            <div>
                <TYPE.desc fontSize={16} fontWeight={500} marginBottom="20px">
                    Total Reward
                </TYPE.desc>
                <TotalPrizeGrid>
                    <PrizeCard>
                        <TYPE.main fontSize={16} fontWeight={500} marginBottom="8px">
                            {totalStats?.totalBTCPrize || '0'} BTC
                        </TYPE.main>
                        <TYPE.desc fontSize={14} color="#999">
                            Total BTC Reward
                        </TYPE.desc>
                    </PrizeCard>
                    <PrizeCard>
                        <TYPE.main fontSize={16} fontWeight={500} marginBottom="8px">
                            {totalStats?.totalUSDTPrize || '0'} USDT
                        </TYPE.main>
                        <TYPE.desc fontSize={14} color="#999">
                            Total USDT Reward
                        </TYPE.desc>
                    </PrizeCard>
                    <PrizeCard>
                        <TYPE.main fontSize={16} fontWeight={500} marginBottom="8px">
                            {totalStats?.totalPointsPrize || '0'} Points
                        </TYPE.main>
                        <TYPE.desc fontSize={14} color="#999">
                            Total Points Reward
                        </TYPE.desc>
                    </PrizeCard>
                    {/* <PrizeCard>
                        <TYPE.main fontSize={16} fontWeight={500} marginBottom="8px">
                            {totalStats?.totalNFTPrize || '0'} NFT
                        </TYPE.main>
                        <TYPE.desc fontSize={14} color="#999">
                            Total NFT Reward
                        </TYPE.desc>
                    </PrizeCard> */}
                    <PrizeCard>
                        <TYPE.main fontSize={16} fontWeight={500} marginBottom="8px">
                            {totalStats?.totalGOATEDPrize || '0'} GOATED
                        </TYPE.main>
                        <TYPE.desc fontSize={14} color="#999">
                            Total GOATED Reward
                        </TYPE.desc>
                    </PrizeCard>
                </TotalPrizeGrid>
            </div>
            {/* Divider */}
            <div
                style={{
                    width: '100%',
                    height: '1px',
                    backgroundImage:
                        'linear-gradient(90deg, #000000 0%, #6A0DAD 25%, #F7931A 50%, #6A0DAD 75%, #000000 100%)',
                    mask: 'repeating-linear-gradient(90deg, black 0px, black 4px, transparent 4px, transparent 8px)',
                    WebkitMask:
                        'repeating-linear-gradient(90deg, black 0px, black 4px, transparent 4px, transparent 8px)',
                    margin: '40px 0',
                }}
            />
            {/* History Details Section */}
            <div>
                <TYPE.desc fontSize={16} fontWeight={500} marginBottom="20px">
                    History details
                </TYPE.desc>
                <TableContainer>
                    {loading.histories ? (
                        <LoadingWrapper style={{ minHeight: '240px' }}>
                            <CircularProgress />
                        </LoadingWrapper>
                    ) : historyRecords.length === 0 ? (
                        <div
                            style={{ height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <TYPE.desc fontSize={16} textAlign="center" marginTop="40px" color="#999">
                                No history records found
                            </TYPE.desc>
                        </div>
                    ) : (
                        <TableContent>
                            {historyRecords.map((record, index) => {
                                // Calculate round number (reverse order, highest first)
                                // Use totalCount from API, falling back to relative calculation if not available
                                let roundNumber: number;
                                if (historiesTotalCount > 0) {
                                    roundNumber = historiesTotalCount - currentPage * pageSize - index;
                                } else {
                                    // No totalCount yet, use relative number
                                    roundNumber = (currentPage + 1) * pageSize - index;
                                }

                                // Ensure rewards is an array
                                const rewards = Array.isArray(record.rewards) ? record.rewards : [];

                                return (
                                    <HistoryRow key={record.id || index}>
                                        <DrawInfo>
                                            <DrawInfoWrapper>
                                                <DrawNumber>#{roundNumber}</DrawNumber>
                                                <DrawCountText>{record.drawCount || 0} draws</DrawCountText>
                                            </DrawInfoWrapper>
                                        </DrawInfo>
                                        {PRIZE_TYPES.map((prizeType) => {
                                            const prize = getPrizeByType(rewards, prizeType);
                                            const { value, color } = formatRewardValue(prize, rewards, prizeType);
                                            return (
                                                <RewardColumn key={prizeType}>
                                                    <RewardColumnWrapper>
                                                        <RewardValue $color={color}>{value}</RewardValue>
                                                        <RewardLabel>{getRewardLabel(prizeType)}</RewardLabel>
                                                    </RewardColumnWrapper>
                                                </RewardColumn>
                                            );
                                        })}
                                        <ActionColumn onClick={() => record.txHash && handleShareClick(record.txHash)}>
                                            <ShareIcon />
                                        </ActionColumn>
                                    </HistoryRow>
                                );
                            })}
                        </TableContent>
                    )}
                </TableContainer>

                {/* Pagination component */}
                {historiesTotalCount > pageSize && (
                    <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
                        <MaterialTablePagination
                            {...({
                                page: currentPage,
                                count: historiesTotalCount,
                                pageSize: pageSize,
                                onPageChange: handlePageChange,
                            } as any)}
                        />
                    </div>
                )}
            </div>
        </HistoryContainer>
    );
}

export default observer(History);
